import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Info, Filter, X, Settings } from 'lucide-react';
import { ProjectEmailService } from '../services/projectEmailService';
import { SearchService } from '../services/searchService';
import { useDebounce } from '../utils/debounce';
import ProjectEmailModal from './ProjectEmailModal';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import { parseCurrentUrl, createNavigationHelper } from '../utils/urlManager';
import { Table, Button, Badge, SearchBar, Popover, Checkbox, FilterChip } from './ui';
import CustomFieldFilter from './CustomFieldFilter';

/**
 * ProjectEmailsBoard
 *
 * Standardized project emails table using the new UI component library.
 * Reference implementation: ContactsTable
 *
 * Features:
 * - Server-side pagination (20/50/All)
 * - Search with project filter
 * - Column sorting
 * - Column resizing with auto-size
 * - Row click to view email details
 * - Real-time email data
 */
const ProjectEmailsBoard = () => {
  // State management
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [filters, setFilters] = useState({
    emailType: ''
  });
  const [customFilters, setCustomFilters] = useState([]);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('project-emails-visible-columns');
    return saved ? JSON.parse(saved) : {
      subject: true,
      from_address: true,
      projectName: true,
      email_type: true,
      summary: true,
      received_at: true,
    };
  });

  // Project search state
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectSearchResults, setProjectSearchResults] = useState([]);
  const [projectSearchLoading, setProjectSearchLoading] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  // Sorting state
  const [sortField, setSortField] = useState('received_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Navigation setup
  const location = useLocation();
  const navigate = useNavigate();
  const navigationHelper = createNavigationHelper(navigate);
  const isClosingRef = useRef(false);

  // URL parsing
  const { id: urlEmailId } = parseCurrentUrl(location.pathname);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Debounced project search
  const debouncedProjectSearch = useDebounce(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setProjectSearchResults([]);
      setShowProjectDropdown(false);
      return;
    }

    setProjectSearchLoading(true);
    try {
      const { data: searchResults } = await SearchService.searchProjects(searchTerm, 15);
      setProjectSearchResults(searchResults || []);
      setShowProjectDropdown(true);
    } catch (error) {
      console.error('Error searching projects:', error);
      setProjectSearchResults([]);
    } finally {
      setProjectSearchLoading(false);
    }
  }, 300);

  // Load emails
  const loadEmails = async () => {
    try {
      setLoading(true);
      setError(null);

      let emailsData;
      if (selectedProject) {
        emailsData = await ProjectEmailService.getEmailsByProjectId(selectedProject.id);
      } else {
        emailsData = await ProjectEmailService.getAllProjectEmails();
      }

      setEmails(emailsData);
    } catch (err) {
      console.error('Error loading emails:', err);
      setError('Failed to load project emails. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load emails on mount and when project filter changes
  useEffect(() => {
    loadEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedProject]);

  // Handle project search input changes
  const handleProjectSearchChange = (e) => {
    const value = e.target.value;
    setProjectSearchTerm(value);
    debouncedProjectSearch(value);
  };

  // Handle selecting a project from search results
  const handleProjectSelect = (project) => {
    const selectedProjectData = {
      id: project.id,
      job_number: project.jobNumber,
      name: project.projectName
    };
    setSelectedProject(selectedProjectData);
    setProjectSearchTerm(`${project.jobNumber} - ${project.projectName}`);
    setShowProjectDropdown(false);
  };

  // Clear project selection
  const clearProjectSelection = () => {
    setSelectedProject(null);
    setProjectSearchTerm('');
    setProjectSearchResults([]);
    setShowProjectDropdown(false);
  };

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProjectDropdown && !event.target.closest('.project-filter-container')) {
        setShowProjectDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProjectDropdown]);

  // Helper function to apply custom filters
  const applyCustomFilter = (email, filter) => {
    const fieldValue = email[filter.field];
    const filterValue = filter.value;

    // Handle array fields
    if (Array.isArray(fieldValue)) {
      const arrayString = fieldValue.join(' ').toLowerCase();
      switch (filter.condition) {
        case 'equals':
          return arrayString === filterValue.toLowerCase();
        case 'contains':
          return arrayString.includes(filterValue.toLowerCase());
        case 'not_contains':
          return !arrayString.includes(filterValue.toLowerCase());
        case 'is_empty':
          return fieldValue.length === 0 || fieldValue.every(v => !v || v.trim() === '');
        case 'is_not_empty':
          return fieldValue.length > 0 && fieldValue.some(v => v && v.trim() !== '');
        case 'starts_with':
          return arrayString.startsWith(filterValue.toLowerCase());
        case 'ends_with':
          return arrayString.endsWith(filterValue.toLowerCase());
        case 'not_equals':
          return arrayString !== filterValue.toLowerCase();
        default:
          return true;
      }
    }

    // Handle string/null fields
    const stringValue = fieldValue ? fieldValue.toString().toLowerCase() : '';
    const filterValueLower = filterValue ? filterValue.toLowerCase() : '';

    switch (filter.condition) {
      case 'equals':
        return stringValue === filterValueLower;
      case 'contains':
        return stringValue.includes(filterValueLower);
      case 'not_contains':
        return !stringValue.includes(filterValueLower);
      case 'starts_with':
        return stringValue.startsWith(filterValueLower);
      case 'ends_with':
        return stringValue.endsWith(filterValueLower);
      case 'is_empty':
        return !fieldValue || fieldValue.toString().trim() === '';
      case 'is_not_empty':
        return fieldValue && fieldValue.toString().trim() !== '';
      case 'not_equals':
        return stringValue !== filterValueLower;
      default:
        return true;
    }
  };

  // Filter emails by search term
  const filteredAndSortedEmails = useMemo(() => {
    let filtered = emails;

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(email =>
        (email.subject && email.subject.toLowerCase().includes(searchLower)) ||
        (email.from_address && email.from_address.toLowerCase().includes(searchLower)) ||
        (email.summary && email.summary.toLowerCase().includes(searchLower)) ||
        (email.email_type && email.email_type.toLowerCase().includes(searchLower)) ||
        (email.projects && email.projects.name && email.projects.name.toLowerCase().includes(searchLower)) ||
        (email.projects && email.projects.job_number && email.projects.job_number.toLowerCase().includes(searchLower))
      );
    }

    // Apply email type filter
    if (filters.emailType) {
      filtered = filtered.filter(email => email.email_type === filters.emailType);
    }

    // Apply custom field filters
    if (customFilters.length > 0) {
      for (const filter of customFilters) {
        filtered = filtered.filter(email => applyCustomFilter(email, filter));
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested project fields
      if (sortField === 'jobNumber') {
        aValue = a.projects?.job_number || '';
        bValue = b.projects?.job_number || '';
      } else if (sortField === 'projectName') {
        aValue = a.projects?.name || '';
        bValue = b.projects?.name || '';
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Convert to strings for comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  }, [emails, searchTerm, sortField, sortDirection, filters, customFilters]);

  // Get unique email types for filter
  const emailTypes = useMemo(() => {
    const types = [...new Set(emails.map(email => email.email_type).filter(Boolean))];
    return types.sort();
  }, [emails]);

  // Pagination: Calculate total pages and get current page data
  const displayedEmails = useMemo(() => {
    const totalFiltered = filteredAndSortedEmails.length;
    const calculatedTotalPages = pageSize === 999999 ? 1 : Math.ceil(totalFiltered / pageSize);

    // Update total pages if it changed
    if (calculatedTotalPages !== totalPages) {
      setTotalPages(calculatedTotalPages);
    }

    // Reset to page 1 if current page is beyond total pages
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }

    // Return all if "All" is selected
    if (pageSize === 999999) {
      return filteredAndSortedEmails;
    }

    // Return current page slice
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedEmails.slice(startIndex, endIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAndSortedEmails, currentPage, pageSize]);

  // Handle row click to open modal
  const handleRowClick = useCallback((email) => {
    // Navigate to email URL
    navigationHelper.goToProjectEmail(email.id);

    setSelectedEmail(email);
    setModalOpen(true);
  }, [navigationHelper]);

  // Handle auto-opening email from URL
  useEffect(() => {
    // Don't re-open modal if we're in the process of closing it
    if (isClosingRef.current) {
      return;
    }

    if (urlEmailId && emails.length > 0) {
      const email = emails.find(e => e.id === urlEmailId);
      if (email && !modalOpen) {
        setSelectedEmail(email);
        setModalOpen(true);
      }
    }
  }, [urlEmailId, emails, modalOpen]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    // Set closing flag to prevent re-opening
    isClosingRef.current = true;

    // Close modal immediately
    setModalOpen(false);
    setSelectedEmail(null);

    // Navigate back to emails list
    navigationHelper.goToProjectEmail();

    // Reset closing flag after navigation
    setTimeout(() => {
      isClosingRef.current = false;
    }, 100);
  }, [navigationHelper]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Handle manual column resize
  const handleManualResize = () => {
    if (autoResizeEnabled) {
      setAutoResizeEnabled(false);
    }
  };

  // Handle sort events from Table component
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Column definitions for Table component
  const columns = [
    {
      id: 'info',
      label: '',
      field: 'id',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      searchable: false,
      resizable: false,
      align: 'center',
      contentType: 'icon',
      render: (value, row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          style={{
            padding: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--gray-400)',
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--gray-800)';
            e.currentTarget.style.color = 'var(--cta-blue)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--gray-400)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title="View email details"
        >
          <Info size={20} />
        </button>
      ),
    },
    {
      id: 'jobNumber',
      label: 'Job #',
      field: 'jobNumber',
      width: 100,
      minWidth: 80,
      maxWidth: 150,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value, row) => (
        row.projects ? (
          <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--gray-50)' }}>
            {row.projects.job_number}
          </span>
        ) : (
          <span style={{ color: 'var(--gray-500)', fontStyle: 'italic' }}>—</span>
        )
      ),
    },
    {
      id: 'projectName',
      label: 'Project Name',
      field: 'projectName',
      width: 200,
      minWidth: 150,
      maxWidth: 350,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value, row) => (
        row.projects ? (
          <span>{row.projects.name}</span>
        ) : (
          <span style={{ color: 'var(--gray-500)', fontStyle: 'italic' }}>Unassigned</span>
        )
      ),
    },
    {
      id: 'from_address',
      label: 'From',
      field: 'from_address',
      width: 200,
      minWidth: 150,
      maxWidth: 300,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'subject',
      label: 'Subject',
      field: 'subject',
      width: 250,
      minWidth: 200,
      maxWidth: 400,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => (
        <span style={{ fontWeight: 'var(--font-medium)' }}>
          {value || '—'}
        </span>
      ),
    },
    {
      id: 'email_type',
      label: 'Type',
      field: 'email_type',
      width: 250,
      minWidth: 150,
      maxWidth: 350,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) => {
        if (!value) return '—';

        // Assign badge variants based on email type
        let variant = 'neutral';
        if (value.includes('Discrepancy') || value.includes('Clarification')) {
          variant = 'warning';
        } else if (value.includes('Revision') || value.includes('Updated')) {
          variant = 'info';
        } else if (value.includes('Confirming')) {
          variant = 'success';
        } else if (value.includes('Status Update')) {
          variant = 'info';
        } else if (value.includes('Admin')) {
          variant = 'neutral';
        }

        return (
          <Badge variant={variant} size="sm">
            {value}
          </Badge>
        );
      },
    },
    {
      id: 'summary',
      label: 'Summary',
      field: 'summary',
      width: 300,
      minWidth: 200,
      maxWidth: 500,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => (
        <span style={{ color: 'var(--gray-300)' }}>
          {value || '—'}
        </span>
      ),
    },
    {
      id: 'received_at',
      label: 'Received',
      field: 'received_at',
      width: 160,
      minWidth: 140,
      maxWidth: 200,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => (
        <span style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>
          {value ? formatDate(value) : '—'}
        </span>
      ),
    },
  ];

  if (loading && emails.length === 0) {
    return <LoadingSpinner message="Loading project emails..." />;
  }

  return (
    <div style={{ padding: '24px', backgroundColor: 'var(--dark-bg)', minHeight: '100vh' }}>
      {/* Error Message */}
      {error && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <ErrorMessage
            message={error}
            onRetry={loadEmails}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-6)',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            Project Emails
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
            }}
          >
            {filteredAndSortedEmails.length} email{filteredAndSortedEmails.length !== 1 ? 's' : ''} found
            {selectedProject && ` for ${selectedProject.job_number} - ${selectedProject.name}`}
          </p>
        </div>
      </div>

      {/* Active Filter Chips */}
      {(filters.emailType || customFilters.length > 0) && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-4)',
        }}>
          {filters.emailType && (
            <FilterChip
              label={`Type: ${filters.emailType}`}
              onRemove={() => setFilters({ ...filters, emailType: '' })}
            />
          )}
          {customFilters.map((filter, index) => (
            <FilterChip
              key={index}
              label={`${filter.field}: ${filter.condition} ${filter.value}`}
              onRemove={() => {
                setCustomFilters(customFilters.filter((_, i) => i !== index));
              }}
            />
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilters({ emailType: '' });
              setCustomFilters([]);
            }}
            style={{
              fontSize: 'var(--text-sm)',
              padding: '4px 8px',
            }}
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Table with integrated toolbar */}
      <div style={{
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
      }}>
        {/* Toolbar: Search Bar and Filters */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-bg)',
            borderBottom: '1px solid var(--gray-600)',
            gap: 'var(--space-4)',
            flexShrink: 0,
          }}
        >
          <div style={{ width: '300px', flexShrink: 0 }}>
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search all emails..."
              size="sm"
            />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
            <Popover
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={16} />
                  Filters
                </Button>
              }
              position="bottom-right"
              width="500px"
              maxWidth="90vw"
              zIndex={1000}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <h3 style={{
                  margin: 0,
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)'
                }}>
                  Filter Project Emails
                </h3>

                {/* Project Filter */}
                <div className="project-filter-container" style={{ position: 'relative' }}>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                    marginBottom: 'var(--space-2)',
                  }}>
                    Filter by Project
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Search projects by name, job #..."
                      value={projectSearchTerm}
                      onChange={handleProjectSearchChange}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        paddingRight: selectedProject ? '40px' : '12px',
                        border: '1px solid var(--gray-600)',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--dark-elevated)',
                        color: 'var(--gray-100)',
                        fontSize: 'var(--text-sm)',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (!e.target.value) {
                          e.target.style.borderColor = 'var(--gray-500)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (document.activeElement !== e.target) {
                          e.target.style.borderColor = 'var(--gray-600)';
                        }
                      }}
                      onFocus={(e) => {
                        e.target.style.outline = 'none';
                        e.target.style.borderColor = 'var(--cta-blue)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                        // Show dropdown if search term is long enough
                        if (projectSearchTerm.length >= 2) {
                          setShowProjectDropdown(true);
                        }
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--gray-600)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    {selectedProject && (
                      <button
                        onClick={clearProjectSelection}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '4px',
                          border: 'none',
                          background: 'none',
                          color: 'var(--gray-400)',
                          cursor: 'pointer',
                          borderRadius: 'var(--radius-sm)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = 'var(--gray-200)';
                          e.currentTarget.style.background = 'var(--gray-700)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--gray-400)';
                          e.currentTarget.style.background = 'none';
                        }}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  {/* Selected Project Display */}
                  {selectedProject && (
                    <div style={{
                      marginTop: 'var(--space-2)',
                      padding: '8px 12px',
                      background: 'var(--dark-elevated)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--gray-200)'
                    }}>
                      Filtering: <strong>{selectedProject.job_number} - {selectedProject.name}</strong>
                    </div>
                  )}

                  {/* Project Search Results Dropdown */}
                  {showProjectDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      boxShadow: 'var(--shadow-xl)',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      marginTop: '4px',
                    }}>
                      {projectSearchLoading ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: '12px 16px',
                          color: 'var(--gray-400)',
                        }}>
                          <Search size={16} className="animate-spin" />
                          <span>Searching projects...</span>
                        </div>
                      ) : projectSearchResults.length > 0 ? (
                        projectSearchResults.map(project => (
                          <button
                            key={project.id}
                            onClick={() => handleProjectSelect(project)}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              textAlign: 'left',
                              borderBottom: '1px solid var(--gray-700)',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'var(--gray-800)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'none';
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--gray-50)', fontSize: 'var(--text-sm)' }}>
                                {project.jobNumber}
                              </span>
                              <span style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)' }}>
                                {project.projectName}
                              </span>
                              {project.clientName && (
                                <span style={{ color: 'var(--gray-500)', fontSize: 'var(--text-xs)', fontStyle: 'italic' }}>
                                  {project.clientName}
                                </span>
                              )}
                            </div>
                          </button>
                        ))
                      ) : projectSearchTerm.length >= 2 ? (
                        <div style={{
                          padding: '12px 16px',
                          color: 'var(--gray-500)',
                        }}>
                          No projects found
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* Email Type Filter */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                    marginBottom: 'var(--space-2)',
                  }}>
                    Filter by Type
                  </label>
                  <select
                    value={filters.emailType}
                    onChange={(e) => setFilters({ ...filters, emailType: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--dark-elevated)',
                      color: 'var(--gray-100)',
                      fontSize: 'var(--text-sm)',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">All Types</option>
                    {emailTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Custom Filters */}
                <div style={{
                  borderTop: '1px solid var(--gray-700)',
                  paddingTop: 'var(--space-4)'
                }}>
                  <h4 style={{
                    margin: 0,
                    marginBottom: 'var(--space-3)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-300)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Advanced Filters
                  </h4>
                  <CustomFieldFilter
                    currentFilters={customFilters}
                    onFiltersChange={setCustomFilters}
                  />
                </div>

                {/* Footer Actions */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: 'var(--space-3)',
                  borderTop: '1px solid var(--gray-700)'
                }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      clearProjectSelection();
                      setFilters({ emailType: '' });
                      setCustomFilters([]);
                    }}
                    disabled={!selectedProject && !filters.emailType && customFilters.length === 0}
                  >
                    Clear all filters
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowFilters(false)}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </Popover>

            <Popover
              isOpen={showColumnSettings}
              onClose={() => setShowColumnSettings(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowColumnSettings(!showColumnSettings);
                    setShowFilters(false);
                  }}
                >
                  <Settings size={16} />
                  Columns
                </Button>
              }
              position="bottom-right"
              zIndex={1000}
            >
              <div style={{
                padding: 'var(--space-4)',
                minWidth: '280px',
                maxWidth: '320px',
              }}>
                <h3 style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                }}>
                  Manage Columns
                </h3>

                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  paddingRight: 'var(--space-2)'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-2)'
                  }}>
                    {columns.filter(col => col.id !== 'info').map(col => (
                      <Checkbox
                        key={col.id}
                        label={col.label}
                        checked={visibleColumns[col.id] !== false}
                        onChange={(e) => {
                          const newVisibleColumns = {
                            ...visibleColumns,
                            [col.id]: e.target.checked
                          };
                          setVisibleColumns(newVisibleColumns);
                          localStorage.setItem('project-emails-visible-columns', JSON.stringify(newVisibleColumns));
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </Popover>

            <Button
              variant={autoResizeEnabled ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setAutoResizeEnabled(!autoResizeEnabled)}
            >
              {autoResizeEnabled ? '✓ ' : ''}Auto-resize
            </Button>
          </div>
        </div>

        {/* Scrollable Table Container */}
        <div style={{
          backgroundColor: 'var(--dark-surface)',
          overflowX: 'auto',
          overflowY: 'auto',
          flex: 1,
          minWidth: 0,
        }}>
          {/* Table */}
          <Table
          data={displayedEmails}
          columns={columns.filter(col => visibleColumns[col.id] !== false)}
          sortable={true}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          searchable={false}
          resizable={true}
          paginated={false}
          autoResizeOnDataChange={autoResizeEnabled}
          showAutoSizeButton={false}
          storageKey="project-emails-table"
          loading={loading}
          emptyMessage={
            emails.length === 0 ? (
              selectedProject ?
                `No emails found for project ${selectedProject.job_number} - ${selectedProject.name}` :
                'No project emails available. Project emails need to be populated from an external email source.'
            ) : (
              searchTerm ?
                `No emails match your search: "${searchTerm}"` :
                'No emails found matching your filters'
            )
          }
          onRowClick={handleRowClick}
          onManualResize={handleManualResize}
          variant="dense"
          stickyHeader={true}
          style={{ border: 'none', borderRadius: '0', overflow: 'visible' }}
        />
        </div>

        {/* Frozen Pagination */}
        {filteredAndSortedEmails.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-bg)',
              borderTop: '1px solid var(--gray-600)',
              gap: 'var(--space-4)',
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                {pageSize === 999999 ? (
                  `Showing all ${filteredAndSortedEmails.length} email${filteredAndSortedEmails.length !== 1 ? 's' : ''}`
                ) : (
                  `Page ${currentPage} of ${totalPages} (${filteredAndSortedEmails.length} email${filteredAndSortedEmails.length !== 1 ? 's' : ''})`
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                  Show:
                </span>
                <Button
                  variant={pageSize === 20 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(20)}
                >
                  20
                </Button>
                <Button
                  variant={pageSize === 50 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(50)}
                >
                  50
                </Button>
                <Button
                  variant={pageSize === 999999 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(999999)}
                >
                  All
                </Button>
              </div>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, currentPage + 2);

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </Button>
                    );
                  }

                  return pages;
                })()}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      <ProjectEmailModal
        email={selectedEmail}
        isOpen={modalOpen}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default ProjectEmailsBoard;
